import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useQueryParams } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import {
  Autosuggest,
  Bleed,
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  ListItem,
} from '@fs/zion-ui'
import { ContentCheck, NoticeLoading, ContentLock } from '@fs/zion-icon'
import { useGroupData, useImageOrganizeModes, useImageOrganizeActions } from '../../../../views/imageBrowse/hooks'
import { workflowStatuses } from '../../../../views/imageBrowse/helpers'
import { useSetFocusedIndex } from '../../../groupImageBrowser/hooks'
import useViewMode from '../../../../views/imageBrowse/hooks/useViewMode'
// See comments below
// import getTitleFromGroup from '../views/imageBrowse/helpers/getTitleFromGroup'

export const query = gql`
  query ImageOrganizeStatusList($rmsIdList: [ID]!) {
    imageOrganizeStatusList(rmsIdList: $rmsIdList) {
      id
      divide {
        status
        userId
      }
      correctMetadata {
        status
        userId
      }
    }
  }
`

export default function ViewAnotherItemDialog({ overlay }) {
  const { cisId } = useUser()
  const { close: closeOverlay, isOpen } = overlay
  const { initialGroup, setGroupIndex } = useGroupData()
  const { viewMode } = useViewMode()
  const { getSkippedStatus, dividerOffsets } = useImageOrganizeActions()
  const { isMetadataAllMode, isMetadataSingleMode, isDivideMode } = useImageOrganizeModes()

  const setFocusIndex = useSetFocusedIndex()

  const [fetchImageOrganizeStatuses, { data: statusData, loading }] = useLazyQuery(query)

  const { setQuery } = useQueryParams()

  const [t] = useTranslation()
  const headingText = t('ViewAnotherItemDialog.navigateHeading', 'Navigate between items')
  // See comment regaurding subHeading below
  // const subHeadingText = (place) =>
  //   t('ViewAnotherItemDialog.subHeaderTest', 'View other items within "{place}"', { place })
  const browseImagesButtonText = t('ViewAnotherItemDialog.browseImagesButtonText', 'Browse Images')
  const cancelButtonText = t('ViewAnotherItemDialog.cancelButtonText', 'Cancel')
  const autoSuggestLabel = t('ViewAnotherItemDialog.autoSuggestLabel', 'Item Number')
  const autoSuggestPlaceholder = t('ViewAnotherItemDialog.autoSuggestPlaceholder', 'Type or choose from list')
  const helpMessage = t(
    'ViewAnotherItemDialog.helpNavigateMessage',
    `The original image group has been divided into {number} groups of images called "items."`,
    {
      number: isDivideMode ? (dividerOffsets?.length ?? 0) : (initialGroup?.childGroups?.length ?? 0),
    }
  )

  const [selectedItem, setSelectedItem] = useState()
  const { imageOrganizeStatusList } = statusData ?? {}

  const suggestions = useMemo(() => {
    const skippedText = t('ViewAnotherItemDialog.skippedText', 'Skipped')
    const itemText = (itemNumber) => t('ViewAnotherItemDialog.itemText', `Item {itemNumber}`, { itemNumber })
    let groups
    if (isDivideMode) groups = dividerOffsets
    else if (initialGroup.id) groups = initialGroup?.childGroups?.length ? initialGroup.childGroups : [initialGroup]
    else groups = []

    return groups.map((group, index) => {
      let rightElement
      if (isMetadataSingleMode || isMetadataAllMode) {
        if (loading) {
          rightElement = <ListItem.Icon Icon={NoticeLoading} />
        } else if (imageOrganizeStatusList?.[index]?.correctMetadata?.status === workflowStatuses.COMPLETED) {
          rightElement = <ListItem.Icon Icon={ContentCheck} />
        } else if (getSkippedStatus(group.id)) {
          rightElement = <ListItem.MetaText text={skippedText} />
        } else if (
          imageOrganizeStatusList?.[index]?.correctMetadata?.status === workflowStatuses.IN_PROGRESS &&
          imageOrganizeStatusList?.[index]?.correctMetadata?.userId !== cisId
        ) {
          rightElement = <ListItem.Icon Icon={ContentLock} />
        }
      }

      return {
        key: isDivideMode ? group : group?.id,
        primaryText: itemText(index + 1),
        index,
        /* ONLY use rightElement if correcting metadata */
        rightElement,
        itemIndex: isDivideMode ? group : null,
        itemApid: group?.imageApids?.[0],
      }
    })
  }, [
    t,
    initialGroup,
    isDivideMode,
    dividerOffsets,
    isMetadataSingleMode,
    isMetadataAllMode,
    loading,
    imageOrganizeStatusList,
    getSkippedStatus,
    cisId,
  ])

  useEffect(() => {
    if (isOpen && initialGroup?.childGroups) {
      // The overlay has opened and is currently transitioning in
      const childIdList = initialGroup?.childGroups.map((group) => group.id)
      fetchImageOrganizeStatuses({ variables: { rmsIdList: childIdList } })
    }
  }, [fetchImageOrganizeStatuses, isOpen, initialGroup])

  const handleBrowseImagesClick = useCallback(() => {
    const newGroupIndex = selectedItem?.index
    if (isDivideMode) {
      // setting groupIndex to show the correct item and metadata
      setGroupIndex(newGroupIndex)
      // setting focusIndex to show the correct image
      setFocusIndex(selectedItem?.itemIndex, null, newGroupIndex)
    } else if (selectedItem) {
      setQuery({ groupId: selectedItem.key })
      setGroupIndex(newGroupIndex)
      if (viewMode === 'single') {
        setFocusIndex(0, null, newGroupIndex)
      } else {
        setFocusIndex(dividerOffsets[newGroupIndex], null, newGroupIndex)
      }
    }
    closeOverlay()
  }, [isDivideMode, selectedItem, closeOverlay, setFocusIndex, setGroupIndex, setQuery, dividerOffsets, viewMode])

  return (
    // Add subtitle back in if/when we can ask zion to truncate the string or allow a node to be passed in
    // <DialogOverlay {...overlay} title={headingText} subtitle={subHeadingText(getTitleFromGroup(initialGroup))}>
    <DialogOverlay {...overlay} title={headingText}>
      <Bleed bottom>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionSelected={setSelectedItem}
          label={autoSuggestLabel}
          placeholder={autoSuggestPlaceholder}
          helpMessage={helpMessage}
        />
      </Bleed>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button onClick={closeOverlay}>{cancelButtonText}</Button>
          <Button emphasis="high" disabled={!selectedItem} onClick={handleBrowseImagesClick}>
            {browseImagesButtonText}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
